// @file Landside navigation store
import { trackPageview } from '@@/bits/analytics'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { captureLandsidePostHogEvent, landsideEventCategoryForPage } from '@@/bits/landside_analytics'
import { getPathnameItems } from '@@/bits/location'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { getTaUrlWithUtmParams } from '@@/bits/ta'
import { ClassroomPlanPricingExperimentVariant, PricingPlansExperimentVariant } from '@@/enums'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import type { User, WallGalleryTemplate } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum LandsidePage {
  Home = 'home',
  Product = 'product',
  Subscriptions = 'subscriptions',
  Templates = 'templates',
  Help = 'help',
  Careers = 'careers',
  WhatsNew = 'whatsnew',
  Pricing = 'pricing',
  DynamicLandingPage = 'dynamic_landing_page',
  Board = 'board',
  Sandbox = 'sandbox',
  Education = 'education',
  PadletArcade = 'PadletArcade',
}

/** The pages Maggi's router serves at their padlet.com URLs — all four
 *  launched pages. The router splits those paths per visitor (office
 *  allow-list first, then a percentage), so a page being listed here moves
 *  nobody to Maggi by itself: it only makes the click a real request the
 *  router can see. This is the only list of what has moved — `landsidePages`
 *  asks `migratedPageHrefMode` about its own key rather than restating it. */
const MIGRATED_PAGES: readonly LandsidePage[] = [
  LandsidePage.Board,
  LandsidePage.Sandbox,
  LandsidePage.Education,
  LandsidePage.Subscriptions,
]

/**
 * How a link to a landing page that has moved off Rails must navigate.
 *
 * Maggi's router owns these paths on padlet.com and picks Rails or Maggi per
 * visitor. A header click that only pushStates never leaves this app, so the
 * router never sees it. `Default` lets the anchor navigate.
 *
 * One flip (`landsideWorkerNavigation`) decides. There is no second
 * preview toggle. Off, every link behaves as it did before this change.
 *
 * Only pages in `MIGRATED_PAGES` leave the SPA. Every other page asks too and
 * is told to stay, so nothing outside that constant decides what has moved.
 */
function migratedPageHrefMode(page: LandsidePage): OzBaseButtonHrefMode {
  return MIGRATED_PAGES.includes(page) && isAppUsing('landsideWorkerNavigation')
    ? OzBaseButtonHrefMode.Default
    : OzBaseButtonHrefMode.HistoryPushState
}

/** An entry in the header, footer and side-panel navigation. */
interface LandsideNavigationPage {
  key: LandsidePage
  text: string
  href: string
  hrefMode: OzBaseButtonHrefMode
  iconName?: string
}

/** The same entry as written down, before the migration decides how it navigates. */
type LandsideNavigationPageDefinition = Omit<LandsideNavigationPage, 'hrefMode'> & {
  hrefMode?: OzBaseButtonHrefMode
}

/**
 * Whether a click on this page's link is fetching a new document rather than making an
 * in-app transition.
 *
 * `Default` mode does not stop a component's `@click` handler, so without this the SPA
 * transition still runs alongside the real navigation, and all of it is wrong:
 *
 * - it swaps in this app's component for a page the Worker is about to serve, showing
 *   the visitor the copy this migration exists to retire until the document lands;
 * - it emits a RudderStack and a PostHog pageview that the destination emits again on
 *   load (`shared/_pepin_analytics.html.erb` fires `rudderanalytics.page` on every
 *   Rails document, and Worker routes roll out independently of this flip), so migrated
 *   links double-count — and the PostHog capture records `window.location.href`, which
 *   is still the page being left behind;
 * - for Subscriptions it also arms the `willReloadForSubscriptions` reload, which races
 *   the anchor's navigation and, since PostHog is pre-warmed and the capture settles in
 *   a few ms, can land first and reload the page the visitor just clicked away from.
 *
 * The click analytics live in the components (`trackEvent`, `trackLandsideEvent`) and
 * are untouched: what a full navigation drops is the SPA transition, not the click.
 */
function navigatesForReal(page: LandsidePage): boolean {
  return migratedPageHrefMode(page) === OzBaseButtonHrefMode.Default
}

interface PageChangeOptions {
  /**
   * Whether this page change is the browser handing back a URL it already had
   * (`popstate`) rather than a click leaving for a new document.
   *
   * `navigatesForReal` skips the SPA transition because the document is about to be
   * replaced. On Back there is no such navigation — this document is staying, and the
   * URL the browser just restored is now the only thing describing it. Skipping the
   * transition there leaves a migrated page's URL over the previous page's content, so
   * the guard has to stand down and let the app render what the URL says.
   */
  isRestoringHistory?: boolean
}

enum ProductPageTab {
  HOW_IT_WORKS = 'howitworks',
  EDUCATION = 'education',
}

enum TemplatesPageTab {
  GENERAL = 'general',
  EDUCATION = 'education',
  BUSINESS = 'business',
}

// Referenced from app/controllers/landside_controller.rb
const landsidePageTitles = {
  [LandsidePage.Home]: __('Creative tools for visual thinkers and learners'),
  [LandsidePage.Product]: __('Product'),
  [LandsidePage.Templates]: __('Templates'),
  [LandsidePage.Subscriptions]: __('Subscriptions'),
  [LandsidePage.Pricing]: __('Pricing'),
  [LandsidePage.DynamicLandingPage]: __('Creative tools for visual thinkers and learners'),
  [LandsidePage.Sandbox]: __('Sandbox'),
  [LandsidePage.Education]: __('Education'),
  [LandsidePage.Board]: __('Board'),
}

const productPageTabTitles = {
  [ProductPageTab.EDUCATION]: __('Padlet - Education'),
  [ProductPageTab.HOW_IT_WORKS]: __('Padlet - How it works'),
}

const templatesPageTabTitles = {
  [TemplatesPageTab.GENERAL]: __('Templates'),
  [TemplatesPageTab.EDUCATION]: __('Templates for education'),
  [TemplatesPageTab.BUSINESS]: __('Templates for business'),
}

const templatesPageTabDescriptions = {
  [TemplatesPageTab.GENERAL]: __(
    'Browse over 250 beautiful, free templates. Save time with easy-to-use boards designed for education, business, and beyond.',
  ),
  [TemplatesPageTab.EDUCATION]: __('Step-by-step guides for creating padlets for education.'),
  [TemplatesPageTab.BUSINESS]: __('Step-by-step guides for creating padlets for business.'),
}

// Upper bound on how long a reload-navigation waits for the PostHog pageview capture to be
// handed off. Warm clients settle in a microtask; this only bites when the lazy client is
// still loading, trading at most this delay for not losing the pageview.
const RELOAD_PAGEVIEW_GRACE_MS = 500

const useLandsideNavigationStore = defineStore('landsideNavigation', () => {
  // State

  const currentPage = ref(LandsidePage.Home)
  const isUserLoggedIn = ref(false)
  const currentUser = ref<User | null>(null)
  const productPageTab = ref<ProductPageTab>(ProductPageTab.HOW_IT_WORKS)
  const pricingPlansExperimentVariant = ref<PricingPlansExperimentVariant>(PricingPlansExperimentVariant.Control)
  const classroomPlanPricingExperimentVariant = ref<ClassroomPlanPricingExperimentVariant>(
    ClassroomPlanPricingExperimentVariant.Control,
  )

  const excludedButtonTitle = ref<string | null>(null)

  // Getters
  const landsidePages = computed((): LandsideNavigationPage[] => {
    // An entry names its own `hrefMode` only when the choice is about something
    // other than the migration: Home follows a signed-in visitor to /dashboard,
    // and Arcade is a different product in a new tab. Everything else is handed
    // its own key, so `MIGRATED_PAGES` stays the single answer to what has moved
    // and a page cannot be listed there yet keep pushStating here.
    const pages: LandsideNavigationPageDefinition[] = [
      {
        key: LandsidePage.Home,
        text: __('Home'),
        href: '/',
        hrefMode: isUserLoggedIn.value ? OzBaseButtonHrefMode.Default : OzBaseButtonHrefMode.HistoryPushState,
      },
      {
        key: LandsidePage.Board,
        text: __('Board'),
        href: '/site/board',
      },
      {
        key: LandsidePage.Sandbox,
        text: __('Sandbox'),
        href: '/site/sandbox',
      },
      {
        key: LandsidePage.Subscriptions,
        text: excludedButtonTitle.value === LandsidePage.Subscriptions ? __('Pricing') : __('Subscriptions'),
        href: '/site/subscriptions',
      },
      {
        key: LandsidePage.Templates,
        text: __('Templates'),
        href: '/site/templates',
      },
      {
        key: LandsidePage.Education,
        text: __('Education'),
        href: '/site/education',
      },
      {
        key: LandsidePage.PadletArcade,
        text: __('Arcade'),
        iconName: 'external_link',
        href: getTaUrlWithUtmParams('home', null, isUserLoggedIn.value),
        hrefMode: OzBaseButtonHrefMode.NewTab,
      },
    ]
    return pages.map((page) => ({ ...page, hrefMode: page.hrefMode ?? migratedPageHrefMode(page.key) }))
  })

  function initializeState(data: {
    isUserLoggedIn: boolean
    currentUser: User
    pricingPlansExperimentVariant: PricingPlansExperimentVariant
    classroomPlanPricingExperimentVariant: ClassroomPlanPricingExperimentVariant
  }): void {
    isUserLoggedIn.value = data.isUserLoggedIn ?? isUserLoggedIn.value
    currentUser.value = data.currentUser ?? currentUser.value
    pricingPlansExperimentVariant.value = data.pricingPlansExperimentVariant ?? pricingPlansExperimentVariant.value
    classroomPlanPricingExperimentVariant.value =
      data.classroomPlanPricingExperimentVariant ?? classroomPlanPricingExperimentVariant.value
    const excludedButtonTitleInStorage = safeLocalStorage.getItem('excludedButtonTitle')
    if (excludedButtonTitleInStorage === null) {
      const chosenPricingText = Math.random() < 0.5 ? LandsidePage.Pricing : LandsidePage.Subscriptions
      excludedButtonTitle.value = chosenPricingText
      safeLocalStorage.setItem('excludedButtonTitle', chosenPricingText)
    } else {
      excludedButtonTitle.value = excludedButtonTitleInStorage
    }
  }

  function setCurrentPage(page: LandsidePage, { isRestoringHistory = false }: PageChangeOptions = {}): void {
    // If user is logged in, they should be directed to /dashboard and we want to prevent the UI from changing
    if (page === LandsidePage.Home && isUserLoggedIn.value) return
    if (page === LandsidePage.Help) return // Prevent the UI from changing since Help is an external url
    if (page === LandsidePage.Careers) return // Prevent the UI from changing since Careers is an external url
    if (page === LandsidePage.WhatsNew) return // Prevent the UI from changing since WhatsNew is an external url
    // The link is fetching its own document, so there is no transition to make here and
    // no pageview to emit — the destination emits its own. See navigatesForReal.
    if (!isRestoringHistory && navigatesForReal(page)) return

    const userFocusedElement = document.activeElement as HTMLElement
    const pricingSectionElement = document.querySelector('[data-testid="landside-subscription-pricing-section"')
    // Navigating to Subscriptions swaps to a full document reload unless the click came from
    // inside the pricing section. Decided up front: the pageview emits below must know a
    // teardown is coming so the PostHog capture isn't dropped mid-flight (see the reload).
    const willReloadForSubscriptions =
      page === LandsidePage.Subscriptions && pricingSectionElement?.contains(userFocusedElement) === false

    currentPage.value = page
    // Separate landside funnel pageviews from the real homepage, which historically shared
    // the 'Homepage' category. The category comes from the same derivation the landside CTA
    // events use, so the five funnel pages get their 'Landside <Page>' category and
    // pageviews join clicks on category as well as the `page` property — and every other
    // page (home, product, pricing, dynamic landing pages) keeps 'Homepage', leaving
    // existing homepage dashboards untouched.
    const pageviewCategory = landsideEventCategoryForPage(page)
    trackPageview(pageviewCategory, page.charAt(0).toUpperCase() + page.slice(1), { page })
    // Mirror the SPA pageview into PostHog. `$pageview` is PostHog's native pageview event
    // (auto-capture is off in our config, so this manual capture is the only source and
    // cannot double-count); `$current_url` is set explicitly because the capture is what
    // PostHog uses to build its pageview reports, and category/page make these pageviews
    // join the landside CTA events captured by trackLandsideEvent. `loggedIn` matches the
    // property the CTA events already carry, so conversion queries can use a comparable
    // logged-out denominator on both sides of the funnel (MAR-815).
    const pageviewCaptured = captureLandsidePostHogEvent('$pageview', {
      $current_url: window.location.href,
      category: pageviewCategory,
      page,
      loggedIn: isUserLoggedIn.value,
    })

    // if user tries to navigate to subscriptions page, it should only reload if user is clicking at the footer
    if (willReloadForSubscriptions) {
      // A synchronous reload would abort the PostHog client's lazy load and drop the capture
      // just made — and the fresh document load emits no PostHog pageview of its own, so the
      // event would be lost outright. Reload once the capture settles (it never rejects),
      // bounded so a slow or failed PostHog load cannot hold the navigation hostage. The
      // navigation itself must survive anything, so an unexpected rejection is reported and
      // still reloads.
      const reload = (): void => location.reload()
      const graceTimeout = new Promise((resolve) => setTimeout(resolve, RELOAD_PAGEVIEW_GRACE_MS))
      void Promise.race([pageviewCaptured, graceTimeout])
        .catch((error) => {
          captureException(error, { context: { source: 'landside_navigation', event: 'pageview_before_reload' } })
        })
        .then(reload)
    }
  }

  function scrollToTop(): void {
    window.scrollTo({
      top: 0,
    })
  }

  function setCurrentPageWithSideEffects(
    page: LandsidePage,
    { isRestoringHistory = false }: PageChangeOptions = {},
  ): void {
    // Guarded here as well as in setCurrentPage, because these side effects are this
    // function's own: retitling and scrolling a document that is about to be replaced
    // shows the visitor the destination's title over the old page's content. See
    // navigatesForReal.
    if (!isRestoringHistory && navigatesForReal(page)) return

    setCurrentPage(page, { isRestoringHistory })

    // Side effects
    scrollToTop()

    // Update document title
    document.title = landsidePageTitles[page]
  }

  function setProductPageTab(tab: ProductPageTab): void {
    productPageTab.value = Object.values(ProductPageTab).includes(tab) ? tab : ProductPageTab.HOW_IT_WORKS
  }

  function setProductPageTabWithSideEffects(tab: ProductPageTab): void {
    setProductPageTab(tab)

    // Side effects
    scrollToTop()

    // Update document title
    document.title = productPageTabTitles[tab]
  }

  function updateTemplatePageTabMetadata(tab: TemplatesPageTab): void {
    // Update document title
    document.title = templatesPageTabTitles[tab]
    // Update document description
    document.querySelector('meta[name="description"]')?.setAttribute('content', templatesPageTabDescriptions[tab])
  }

  function updateTemplatePageMetadata(wallTemplate: WallGalleryTemplate): void {
    // Update document title
    document.title = __('%{wallTemplateTitle} - Padlet', { wallTemplateTitle: wallTemplate.title })
    // Update document description
    document.querySelector('meta[name="description"]')?.setAttribute('content', wallTemplate.description ?? '')
  }

  function setTemplatesPageTabWithSideEffects(tab: TemplatesPageTab): void {
    // Side effects
    scrollToTop()

    updateTemplatePageTabMetadata(tab)
  }

  /** Bound to `popstate` in LandsideApp.vue — see `PageChangeOptions.isRestoringHistory`
   *  for why these page changes are exempt from the real-navigation guard. */
  function switchPageToUrlParams(): void {
    // The first element in getPathnameItems for Landside returns 'site'
    const pathNameItems = getPathnameItems().filter((pathNameItem) => pathNameItem !== 'site')
    let landsidePage: LandsidePage
    // If path represents a dynamic landing page, we set the landsidePage to DynamicLandingPage
    if (pathNameItems[0] === 'product' && pathNameItems[1] === 'features') {
      landsidePage = LandsidePage.DynamicLandingPage
    } else {
      landsidePage = pathNameItems[0] as LandsidePage
    }
    if (Object.values(LandsidePage).includes(landsidePage)) {
      setCurrentPageWithSideEffects(landsidePage, { isRestoringHistory: true })

      if (landsidePage === LandsidePage.Product) {
        const tab = pathNameItems[1] as ProductPageTab
        setProductPageTab(tab)
        return
      }

      return
    }

    // Fallthrough when landsidePage is not a valid landside page,
    // we set the homepage as the current page
    setCurrentPageWithSideEffects(LandsidePage.Home, { isRestoringHistory: true })
  }

  return {
    // State
    currentPage,
    productPageTab,
    isUserLoggedIn,
    currentUser,
    pricingPlansExperimentVariant,
    classroomPlanPricingExperimentVariant,

    // Getters
    landsidePages,

    // Actions
    initializeState,
    scrollToTop,
    setCurrentPage,
    setCurrentPageWithSideEffects,
    setProductPageTab,
    setProductPageTabWithSideEffects,
    setTemplatesPageTabWithSideEffects,
    updateTemplatePageTabMetadata,
    updateTemplatePageMetadata,
    switchPageToUrlParams,
  }
})
export { LandsidePage, migratedPageHrefMode, ProductPageTab, TemplatesPageTab, useLandsideNavigationStore }
